import * as signalR from '@microsoft/signalr';
import { API_BASE } from './authApi';
import { getAccessToken } from './authStorage';

/**
 * MeetingSignaling wraps the SignalR hub connection and exposes a clean
 * event-based API for the WebRTC layer.
 *
 * Usage:
 *   const sig = new MeetingSignaling();
 *   sig.on('peerJoined', ({ connectionId, userId, displayName }) => ...);
 *   sig.on('receiveOffer', ({ from, sdp }) => ...);
 *   sig.on('receiveAnswer', ({ from, sdp }) => ...);
 *   sig.on('receiveIceCandidate', ({ from, candidateJson }) => ...);
 *   sig.on('peerLeft', ({ connectionId }) => ...);
 *   sig.on('meetingEnded', () => ...);
 *   sig.on('peerMediaState', ({ connectionId, micOn, cameraOn }) => ...);
 *   await sig.start(roomToken);
 *   // later:
 *   await sig.sendOffer(targetId, sdp);
 *   await sig.sendAnswer(targetId, sdp);
 *   await sig.sendIceCandidate(targetId, candidateJson);
 *   await sig.updateMediaState(roomToken, micOn, cameraOn);
 *   await sig.stop();
 */
export class MeetingSignaling {
  constructor() {
    this._listeners = {};
    this._connection = null;
    this._roomToken = null;
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  async start(roomToken, guestName = null, guestPhotoData = null, guestToken = null) {
    this._roomToken = roomToken;
    // Kept so an automatic reconnect can rejoin as the same person instead of
    // an anonymous guest with no name/photo.
    this._guestName = guestName;
    this._guestPhotoData = guestPhotoData;
    // Durable id for the waiting-room approval — a page reload gets a brand-new
    // connectionId, so approval can't be keyed to that alone or every reload
    // would need the host to approve again.
    this._guestToken = guestToken;

    // Build ws:// or wss:// URL from the API base (http→ws, https→wss)
    const wsUrl = `${API_BASE}/hubs/meeting`.replace(/^http/, 'ws');

    this._connection = new signalR.HubConnectionBuilder()
      .withUrl(wsUrl, {
        accessTokenFactory: () => getAccessToken() ?? '',
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,   // avoids HTTP negotiate — token goes via query string
      })
      .withAutomaticReconnect()
      .build();

    this._registerHubHandlers();

    await this._connection.start();
    await this._connection.invoke('JoinRoom', roomToken, guestName, guestPhotoData, guestToken);
  }

  async stop() {
    if (!this._connection) return;
    try {
      if (this._roomToken)
        await this._connection.invoke('LeaveRoom', this._roomToken);
    } catch { /* ignore if already disconnected */ }
    await this._connection.stop();
    this._connection = null;
  }

  retryJoinRoom(guestName, guestPhotoData) {
    this._guestName = guestName ?? this._guestName;
    this._guestPhotoData = guestPhotoData ?? this._guestPhotoData;
    return this._connection.invoke(
      'JoinRoom', this._roomToken, this._guestName, this._guestPhotoData ?? null, this._guestToken ?? null
    );
  }

  get connectionId() {
    return this._connection?.connectionId ?? null;
  }

  // ─── Outgoing ──────────────────────────────────────────────────────────────

  sendOffer(targetConnectionId, sdp) {
    return this._connection.invoke('SendOffer', targetConnectionId, sdp);
  }

  sendAnswer(targetConnectionId, sdp) {
    return this._connection.invoke('SendAnswer', targetConnectionId, sdp);
  }

  sendIceCandidate(targetConnectionId, candidateJson) {
    return this._connection.invoke('SendIceCandidate', targetConnectionId, candidateJson);
  }

  updateMediaState(micOn, cameraOn, screenSharing = false) {
    return this._connection.invoke('UpdateMediaState', this._roomToken, micOn, cameraOn, screenSharing);
  }

  endMeeting() {
    return this._connection.invoke('EndMeeting', this._roomToken);
  }

  /** Host updates the meeting's title/description; broadcast to everyone in the room. */
  updateMeetingDetails(title, goal) {
    return this._connection.invoke('UpdateMeetingDetails', this._roomToken, title, goal);
  }

  // ─── Waiting Room (Host Actions) ───────────────────────────────────────────

  approveGuest(guestToken) {
    return this._connection.invoke('ApproveGuest', this._roomToken, guestToken);
  }

  denyGuest(guestToken) {
    return this._connection.invoke('DenyGuest', this._roomToken, guestToken);
  }

  // ─── Chat ──────────────────────────────────────────────────────────────────

  /**
   * Send a chat message.
   * type: 'text' | 'image' | 'file'
   * content: text body or URL
   */
  sendChatMessage(type, content, fileName = null, fileSize = null) {
    return this._connection.invoke(
      'SendChatMessage', this._roomToken, type, content, fileName, fileSize
    );
  }

  // ─── Voting ────────────────────────────────────────────────────────────────

  /** Host creates a vote/decision popup. options = string[] */
  createVote(title, options) {
    return this._connection.invoke('CreateVote', this._roomToken, title, options);
  }

  /** Cast or change a vote. optionIndex = index into options array. */
  castVote(voteId, optionIndex) {
    return this._connection.invoke('CastVote', this._roomToken, voteId, optionIndex);
  }

  /** Creator shows or hides results. */
  toggleVoteResults(voteId, show) {
    return this._connection.invoke('ToggleVoteResults', this._roomToken, voteId, show);
  }

  /** Creator closes the vote (no more responses allowed). */
  closeVote(voteId) {
    return this._connection.invoke('CloseVote', this._roomToken, voteId);
  }

  /** Creator removes the vote popup from everyone's screen. */
  dismissVote(voteId) {
    return this._connection.invoke('DismissVote', this._roomToken, voteId);
  }

  // ─── Events ────────────────────────────────────────────────────────────────

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  off(event, handler) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter(h => h !== handler);
  }

  _emit(event, payload) {
    (this._listeners[event] ?? []).forEach(h => h(payload));
  }

  // ─── Hub message handlers ──────────────────────────────────────────────────

  _registerHubHandlers() {
    const c = this._connection;

    // Sent once on join: existing peers in the room
    c.on('RoomPeers', peers => this._emit('roomPeers', peers));

    // A new participant joined
    c.on('PeerJoined', data => this._emit('peerJoined', data));

    // A participant left or disconnected
    c.on('PeerLeft', data => this._emit('peerLeft', data));

    // WebRTC signaling messages
    c.on('ReceiveOffer', data => this._emit('receiveOffer', data));
    c.on('ReceiveAnswer', data => this._emit('receiveAnswer', data));
    c.on('ReceiveIceCandidate', data => this._emit('receiveIceCandidate', data));

    // Host ended the meeting
    c.on('MeetingEnded', () => this._emit('meetingEnded'));

    // Host updated the meeting title/description
    c.on('MeetingDetailsUpdated', data => this._emit('meetingDetailsUpdated', data));

    // Mic/camera state from a peer
    c.on('PeerMediaState', data => this._emit('peerMediaState', data));

    // ── Chat ──────────────────────────────────────────────────────────────────
    c.on('ReceiveChatMessage', msg => this._emit('chatMessage', msg));

    // ── Voting ────────────────────────────────────────────────────────────────
    c.on('VoteCreated',   vote => this._emit('voteCreated', vote));
    c.on('VoteUpdated',   vote => this._emit('voteUpdated', vote));
    c.on('VoteDismissed', data => this._emit('voteDismissed', data));

    // ── Waiting Room ──────────────────────────────────────────────────────────
    c.on('GuestWaiting', data => this._emit('guestWaiting', data));
    c.on('WaitingForHostApproval', () => this._emit('waitingForHostApproval'));
    c.on('GuestApproved', () => this._emit('guestApproved'));
    c.on('GuestDenied', () => this._emit('guestDenied'));

    // Server-side errors
    c.on('Error', msg => this._emit('error', msg));

    // Connection state events
    c.onreconnecting(() => this._emit('reconnecting'));
    c.onreconnected(() => {
      this._emit('reconnected');
      // Rejoin the room after reconnect, keeping our identity
      this._connection
        .invoke('JoinRoom', this._roomToken, this._guestName ?? null, this._guestPhotoData ?? null, this._guestToken ?? null)
        .catch(() => {});
    });
    c.onclose(() => this._emit('disconnected'));
  }
}
